import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './MLAdmin.module.css';
import Button from '../../components/Button'
import PageContainer from '../../components/PageElements/PageContainer/PageContainer'
import PageBody from '../../components/PageElements/PageBody/PageBody'
import config from '../../config'

interface IngredientSummary {
  total_ingredients: number;
  confirmed_ingredients: number;
  pending_review: number;
  review_completion_rate: number;
  nutrient_profiles: {
    total: number;
    confirmed: number;
    pending: number;
    completion_rate: number;
  };
  error?: string;
}

interface IngredientData {
  id: string;
  normalization_id: string;
  normalized_name: string;
  category: string;
  dietary_attributes: { [key: string]: boolean };
  nutrient_levels: { [key: string]: string | null };
  nutrient_values_per_100g: { [key: string]: string | null };
  confirmed: boolean;
  overall_confidence: number | null;
  created_at: string;
  updated_at: string;
}


const MLAdmin: React.FC = () => {
  const navigate = useNavigate();
  
  // Dietary attributes are now handled directly in the data structure
  
  const [summary, setSummary] = useState<IngredientSummary | null>(null);
  const [ingredients, setIngredients] = useState<IngredientData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showReviewOnly, setShowReviewOnly] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingIngredient, setEditingIngredient] = useState<IngredientData | null>(null);
  const [editData, setEditData] = useState<{ [key: string]: boolean }>({});
  const [nutrientData, setNutrientData] = useState<{ [key: string]: string }>({});
  const [saving, setSaving] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(25); // Fixed page size for simplicity
  const [totalIngredients, setTotalIngredients] = useState(0);

  useEffect(() => {
    loadIngredientData();
  }, [showReviewOnly, currentPage, pageSize]); // eslint-disable-line react-hooks/exhaustive-deps


  // Cleanup effect to restore body scrolling when component unmounts
  useEffect(() => {
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && editModalOpen) {
        closeEditModal();
      }
    };

    if (editModalOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [editModalOpen]);

  const loadIngredientData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Load summary
      const summaryUrl = `${config.API_BASE_URL}/api/admin/ml/summary`;
      const summaryResponse = await fetch(summaryUrl);
      
      if (!summaryResponse.ok) {
        throw new Error('Failed to load ingredient summary');
      }
      
      const summaryData = await summaryResponse.json();
      setSummary(summaryData);

      // Load ingredients with pagination
      const searchParam = searchTerm ? `&search=${encodeURIComponent(searchTerm)}` : '';
      const ingredientsUrl = showReviewOnly 
        ? `${config.API_BASE_URL}/api/admin/ml/ingredients?needs_review_only=true&page=${currentPage}&page_size=${pageSize}${searchParam}`
        : `${config.API_BASE_URL}/api/admin/ml/ingredients?page=${currentPage}&page_size=${pageSize}${searchParam}`;
      
      const ingredientsResponse = await fetch(ingredientsUrl);
      if (!ingredientsResponse.ok) {
        throw new Error('Failed to load ingredients');
      }
      const ingredientsData = await ingredientsResponse.json();
      
      setIngredients(ingredientsData.ingredients || []);
      setTotalIngredients(ingredientsData.total || 0);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setLoading(false);
    }
  };


  const getStatusColor = (confirmed: boolean) =>
    confirmed ? '#28a745' : '#ffc107';

  // Pagination handlers
  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  // Page size change handler removed for simplicity

  const resetToFirstPage = () => {
    setCurrentPage(1);
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
  };

  const handleSearch = () => {
    resetToFirstPage();
    loadIngredientData();
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    resetToFirstPage();
    loadIngredientData();
  };

  const formatDietaryAttributes = (dietaryAttributes: { [key: string]: boolean }) => {
    return Object.entries(dietaryAttributes)
      .filter(([_, value]) => value === true) // Only show true attributes
      .map(([label, _]) => label.replace('is_', '').replace(/_/g, ' '))
      .join(', ');
  };

  // Edit functions
  const openEditModal = (ingredient: IngredientData) => {
    setEditingIngredient(ingredient);
    setEditData({ ...ingredient.dietary_attributes });
    
    // Convert null values to empty strings for editing
    const nutrientData: { [key: string]: string } = {};
    Object.entries(ingredient.nutrient_values_per_100g || {}).forEach(([key, value]) => {
      nutrientData[key] = value || '';
    });
    setNutrientData(nutrientData);
    
    // Prevent body scrolling when modal is open
    document.body.style.overflow = 'hidden';
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    // Restore body scrolling when modal is closed
    document.body.style.overflow = 'unset';
    setEditModalOpen(false);
    setEditingIngredient(null);
    setEditData({});
    setNutrientData({});
  };

  const handleDietaryAttributeChange = (attribute: string, value: boolean) => {
    setEditData(prev => ({
      ...prev,
      [attribute]: value
    }));
  };

  const handleNutrientLevelChange = (nutrient: string, value: string) => {
    setNutrientData(prev => ({
      ...prev,
      [nutrient]: value
    }));
  };

  const saveChanges = async () => {
    if (!editingIngredient) return;

    setSaving(true);
    try {
      // Update dietary attributes
      const dietaryResponse = await fetch(`${config.API_BASE_URL}/api/admin/ml/ingredients/${editingIngredient.id}/dietary`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(editData)
      });

      if (!dietaryResponse.ok) {
        throw new Error('Failed to update dietary attributes');
      }

      // Convert empty strings to null for nutrient levels
      const cleanedNutrientData: { [key: string]: string | null } = {};
      Object.entries(nutrientData).forEach(([key, value]) => {
        cleanedNutrientData[key] = value === '' ? null : value;
      });

      // Update nutrient levels (per-100g values)
      const nutrientResponse = await fetch(`${config.API_BASE_URL}/api/admin/ml/ingredients/${editingIngredient.id}/nutrients-per-100g`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(cleanedNutrientData)
      });

      if (!nutrientResponse.ok) {
        throw new Error('Failed to update nutrient levels');
      }

      // Refresh data
      await loadIngredientData();
      closeEditModal();
      alert('✅ Ingredient updated successfully!');
    } catch (error) {
      console.error('Failed to save changes:', error);
      alert('Failed to save changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <PageBody>
          <div className={styles.loading}>Loading Ingredient Classifications...</div>
        </PageBody>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <PageBody>
          <div className={styles.error}>
            <h2>Error Loading Ingredient Classifications</h2>
            <p>{error}</p>
            <Button onClick={loadIngredientData}>Retry</Button>
          </div>
        </PageBody>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageBody>
        <div className={styles.mlAdmin}>
          {/* Header */}
          <div className={styles.header}>
            <div className={styles.headerContent}>
              <h1>Ingredient Classification Dashboard</h1>
              <p>Manage ingredient dietary attributes and nutrient levels</p>
            </div>
            <Button 
              onClick={() => navigate('/admin')}
              variant="secondary"
            >
              Back to Admin
            </Button>
          </div>

          {/* Summary Section */}
          <div className={styles.summarySection}>
            <h2>Classification Status</h2>
            {summary && (
              <div className={styles.summaryGrid}>
                <div className={styles.summaryCard}>
                  <h3>System Status</h3>
                  <div 
                    className={styles.statusIndicator}
                    style={{ backgroundColor: '#28a745' }}
                  >
                    ACTIVE
                  </div>
                </div>
                <div className={styles.summaryCard}>
                  <h3>Total Ingredients</h3>
                  <div className={styles.summaryValue}>{summary.total_ingredients}</div>
                </div>
                <div className={styles.summaryCard}>
                  <h3>Needs Review</h3>
                  <div className={styles.summaryValue}>{summary.pending_review}</div>
                </div>
                <div className={styles.summaryCard}>
                  <h3>Review Progress</h3>
                  <div className={styles.summaryValue}>{summary.review_completion_rate.toFixed(1)}%</div>
                </div>
              </div>
            )}
          </div>

          {/* Controls */}
          <div className={styles.controls}>
            <div className={styles.filterControls}>
              <div className={styles.searchContainer}>
                <input
                  type="text"
                  placeholder="Search by ingredient name..."
                  value={searchTerm}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  className={styles.searchInput}
                />
                <button
                  type="button"
                  onClick={handleSearch}
                  className={styles.searchButton}
                >
                  Search
                </button>
                {searchTerm && (
                  <button
                    type="button"
                    onClick={handleClearSearch}
                    className={styles.clearButton}
                  >
                    Clear
                  </button>
                )}
              </div>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={showReviewOnly}
                  onChange={(e) => {
                    setShowReviewOnly(e.target.checked);
                    resetToFirstPage(); // Reset to first page when filter changes
                  }}
                />
                Show only ingredients needing review
              </label>
            </div>
            <Button onClick={loadIngredientData}>
              Refresh Data
            </Button>
          </div>

          {/* Ingredients Table */}
          <div className={styles.predictionsSection}>
            <h2>Ingredient Dietary Attributes & Nutrient Profiles</h2>
            {ingredients.length === 0 ? (
              <div className={styles.emptyState}>
                <p>No ingredients found.</p>
                {searchTerm && (
                  <p>Try adjusting your search term or clearing the search</p>
                )}
                {showReviewOnly && !searchTerm && (
                  <p>Try unchecking "Show only ingredients needing review"</p>
                )}
              </div>
            ) : (
              <div className={styles.tableContainer}>
                <table className={styles.predictionsTable}>
                  <thead>
                    <tr>
                      <th>Ingredient</th>
                      <th>Category</th>
                      <th>Status</th>
                      <th>Dietary Attributes</th>
                      <th>Nutrient Levels</th>
                      <th>Review Status</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ingredients.map((ingredient) => (
                      <tr key={ingredient.id}>
                        <td className={styles.ingredientCell}>
                          <div className={styles.ingredientName}>
                            {ingredient.normalized_name}
                          </div>
                        </td>
                        <td>
                          <span className={styles.category}>{ingredient.category}</span>
                        </td>
                        <td className={styles.confidenceCell}>
                          <div 
                            className={styles.confidenceBar}
                            style={{ 
                              width: '100%',
                              backgroundColor: getStatusColor(ingredient.confirmed)
                            }}
                          />
                          <div className={styles.confidenceText}>
                            {ingredient.confirmed ? 'Confirmed' : 'Pending'}
                          </div>
                        </td>
                        <td className={styles.predictionsCell}>
                          {formatDietaryAttributes(ingredient.dietary_attributes)}
                        </td>
                        <td className={styles.predictionsCell}>
                          {Object.entries(ingredient.nutrient_values_per_100g || {})
                            .filter(([_, value]) => value !== null && value !== '')
                            .map(([nutrient, value]) => (
                              <span key={nutrient} className={styles.nutrientTag}>
                                {nutrient.replace('_per_100g', '')}: {value}
                              </span>
                            ))
                            .slice(0, 3)}
                          {Object.values(ingredient.nutrient_values_per_100g || {}).filter(v => v !== null && v !== '').length > 3 && 
                            <span className={styles.moreNutrients}>+{Object.values(ingredient.nutrient_values_per_100g || {}).filter(v => v !== null && v !== '').length - 3} more</span>
                          }
                        </td>
                        <td>
                          <div className={styles.statusContainer}>
                            <span className={`${styles.statusBadge} ${
                              ingredient.confirmed 
                                ? styles.reviewed 
                                : styles.needsReview
                            }`}>
                              {ingredient.confirmed 
                                ? '✅ Confirmed' 
                                : '🔍 Needs Review'
                              }
                            </span>
                            {!ingredient.confirmed && (
                              <span className={styles.priorityBadge}>
                                HIGH PRIORITY
                              </span>
                            )}
                          </div>
                        </td>
                        <td>
                          {new Date(ingredient.created_at).toLocaleDateString()}
                        </td>
                        <td>
                          <button
                            className={styles.editButton}
                            onClick={() => openEditModal(ingredient)}
                            title="Edit ingredient"
                          >
                            ✏️ Edit
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                
                {/* Pagination Controls */}
                {totalIngredients > pageSize && (
                  <div className={styles.pagination}>
                    <div className={styles.paginationInfo}>
                      <span>
                        Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalIngredients)} of {totalIngredients} ingredients
                      </span>
                    </div>
                    
                    <div className={styles.paginationControls}>
                      <button
                        className={styles.paginationButton}
                        onClick={() => handlePageChange(1)}
                        disabled={currentPage === 1}
                      >
                        First ⏮️
                      </button>
                      <button
                        className={styles.paginationButton}
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        ◀️ Previous
                      </button>
                      
                      <span className={styles.pageInfo}>
                        Page {currentPage} of {Math.ceil(totalIngredients / pageSize)}
                      </span>
                      
                      <button
                        className={styles.paginationButton}
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage >= Math.ceil(totalIngredients / pageSize)}
                      >
                        Next ▶️
                      </button>
                      <button
                        className={styles.paginationButton}
                        onClick={() => handlePageChange(Math.ceil(totalIngredients / pageSize))}
                        disabled={currentPage >= Math.ceil(totalIngredients / pageSize)}
                      >
                        Last ⏭️
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>


          {/* Edit Modal */}
          {editModalOpen && editingIngredient && (
            <div className={styles.modal}>
              <div 
                className={styles.modalOverlay}
                onClick={closeEditModal}
              />
              <div 
                className={styles.modalContent}
                onClick={(e) => e.stopPropagation()}
              >
                <div className={styles.modalHeader}>
                  <h3>Edit Ingredient: {editingIngredient.normalized_name}</h3>
                  <button 
                    className={styles.closeButton}
                    onClick={closeEditModal}
                  >
                    ×
                  </button>
                </div>
                <div className={styles.modalBody}>
                  <div className={styles.editSection}>
                    <h4>Dietary Attributes</h4>
                    <div className={styles.attributesGrid}>
                      {Object.entries(editingIngredient.dietary_attributes).map(([attribute, value]) => (
                        <div key={attribute} className={styles.attributeRow}>
                          <label className={styles.attributeLabel}>
                            <input
                              type="checkbox"
                              checked={editData[attribute] || false}
                              onChange={(e) => handleDietaryAttributeChange(attribute, e.target.checked)}
                            />
                            <span>{attribute.replace('is_', '').replace(/_/g, ' ')}</span>
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className={styles.editSection}>
                    <h4>Nutrient Values (per 100g)</h4>
                    <p className={styles.nutrientDescription}>
                      Enter values per 100g. The system will automatically classify as low/medium/high.
                    </p>
                    <div className={styles.nutrientsGrid}>
                      {Object.entries(editingIngredient.nutrient_values_per_100g || {})
                        .filter(([nutrient, _]) => nutrient !== 'confirmed') // Exclude confirmed field
                        .map(([nutrient, value]) => (
                        <div key={nutrient} className={styles.nutrientRow}>
                          <label className={styles.nutrientLabel}>
                            {nutrient.replace('_per_100g', '').replace(/_/g, ' ')} (per 100g):
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={nutrientData[nutrient] || ''}
                            onChange={(e) => handleNutrientLevelChange(nutrient, e.target.value)}
                            className={styles.nutrientInput}
                            placeholder="Enter value per 100g"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div className={styles.modalFooter}>
                  <Button 
                    onClick={closeEditModal}
                    variant="secondary"
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={saveChanges}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </PageBody>
    </PageContainer>
  );
};

export default MLAdmin;